import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Bug, Lightbulb, Send, Clock, CheckCircle2, CircleDot } from "lucide-react";
import { BrutalButton, BrutalCard, SectionTitle, EmptyState } from "@/components/Brutal";
import { MultiPhotoInput, VoiceButton } from "@/components/AiComponents";
import { toast } from "sonner";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";

const STATUS = {
  open: { label: "Open", cls: "bg-brand-blue text-white", icon: CircleDot },
  in_progress: { label: "In behandeling", cls: "bg-brand-yellow", icon: Clock },
  closed: { label: "Gesloten", cls: "bg-brand-green", icon: CheckCircle2 },
};

export default function Tickets() {
  const navigate = useNavigate();
  const t = useT();
  const [type, setType] = useState("bug");
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [shots, setShots] = useState([]);
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);

  const load = () => api.get("/tickets/mine").then((r) => setMine(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!title.trim() || !desc.trim()) return toast.error(t("Vul een titel en omschrijving in"));
    setBusy(true);
    try {
      await api.post("/tickets", { type, title, description: desc, screenshots: shots });
      toast.success(t("Verzonden! Bedankt voor je melding."));
      setTitle(""); setDesc(""); setShots([]); setType("bug");
      await load();
    } catch (e) { toast.error(t("Verzenden mislukt")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="tickets-page">
      <button onClick={() => navigate("/meer")} className="flex items-center gap-1.5 text-sm font-bold text-brand-ink/60"><ArrowLeft className="w-4 h-4" strokeWidth={2.5} />{t("Terug")}</button>
      <SectionTitle sub={t("Meld een fout of deel een suggestie")}>{t("Fouten & Suggesties")}</SectionTitle>

      <BrutalCard data-testid="ticket-form">
        <div className="grid grid-cols-2 gap-2 mb-3">
          <button data-testid="ticket-type-bug" onClick={() => setType("bug")}
            className={`brutal-sm brutal-press rounded-xl px-3 py-2.5 text-sm font-bold flex items-center justify-center gap-2 ${type === "bug" ? "bg-brand-pink text-white" : "bg-brand-cream"}`}><Bug className="w-4 h-4" strokeWidth={2.5} />{t("Fout melden")}</button>
          <button data-testid="ticket-type-suggestion" onClick={() => setType("suggestion")}
            className={`brutal-sm brutal-press rounded-xl px-3 py-2.5 text-sm font-bold flex items-center justify-center gap-2 ${type === "suggestion" ? "bg-brand-violet text-white" : "bg-brand-cream"}`}><Lightbulb className="w-4 h-4" strokeWidth={2.5} />{t("Suggestie")}</button>
        </div>
        <input data-testid="ticket-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("Titel")}
          className="w-full brutal-sm rounded-xl px-3 py-2.5 font-medium outline-none mb-2" />
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-sm font-bold">{t("Omschrijving")}</p>
          <VoiceButton testid="ticket-desc-voice" onText={(txt) => setDesc((d) => (d && d.trim() ? d.trim() + " " : "") + txt)} />
        </div>
        <textarea data-testid="ticket-desc" value={desc} onChange={(e) => setDesc(e.target.value)} rows={4} placeholder={t("Omschrijf wat er gebeurde of je idee… (of spreek het in)")}
          className="w-full brutal-sm rounded-xl p-3 text-sm outline-none mb-2 min-w-0" />
        {type === "bug" && (
          <div className="mb-3">
            <p className="text-sm font-bold mb-1.5">{t("Screenshots (optioneel)")}</p>
            <MultiPhotoInput value={shots} onChange={setShots} max={3} testid="ticket-shots" />
          </div>
        )}
        <BrutalButton data-testid="ticket-submit" onClick={submit} disabled={busy} className="w-full"><Send className="w-4 h-4" strokeWidth={2.5} />{busy ? t("Bezig…") : t("Versturen")}</BrutalButton>
      </BrutalCard>

      <SectionTitle>{t("Mijn meldingen")}</SectionTitle>
      {mine.length === 0 ? (
        <EmptyState icon={Bug} title={t("Nog geen meldingen")} text={t("Jouw fouten en suggesties verschijnen hier.")} />
      ) : (
        <div className="space-y-3">
          {mine.map((tk) => {
            const st = STATUS[tk.status] || STATUS.open;
            const StIcon = st.icon;
            return (
              <BrutalCard key={tk.id} data-testid={`ticket-${tk.id}`}>
                <div className="flex items-start gap-2">
                  {tk.type === "bug" ? <Bug className="w-5 h-5 mt-0.5 text-brand-pink shrink-0" strokeWidth={2.5} /> : <Lightbulb className="w-5 h-5 mt-0.5 text-brand-violet shrink-0" strokeWidth={2.5} />}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-head font-bold leading-tight">{tk.title}</h3>
                    <p className="text-sm text-brand-ink/70 mt-0.5">{tk.description}</p>
                    {tk.admin_note && <p className="text-xs mt-2 brutal-sm rounded-lg bg-brand-cream p-2"><b>{t("Reactie beheer")}:</b> {tk.admin_note}</p>}
                  </div>
                  <span className={`brutal-sm rounded-full px-2.5 py-1 text-xs font-bold flex items-center gap-1 shrink-0 ${st.cls}`}><StIcon className="w-3 h-3" strokeWidth={2.5} />{t(st.label)}</span>
                </div>
              </BrutalCard>
            );
          })}
        </div>
      )}
    </div>
  );
}
